import Store from '../store';
import window from '../window';
import Constants from './constants';
export default class Helpers {
    /**
     * Turn a byte to a hex string
     * @param byteArray The byte array
     */
    static toHexString(byteArray) {
        return ('0x' +
            Array.prototype.map
                .call(byteArray, (byte) => {
                return ('0' + (byte & 0xff).toString(16)).slice(-2);
            })
                .join(''));
    }
    /**
     * Generates 32 byte of random value
     */
    static get32ByteRandomValue() {
        return this.toHexString(window.crypto.getRandomValues(new Uint8Array(32)));
    }
    /**
     * Gets the wallet url
     */
    static getWalletURL() {
        let walletURL = '';
        const scripts = document.getElementsByTagName('script');
        for (let i = 0; i < scripts.length; i++) {
            if (scripts[i].src.includes('fun-wallet-sdk.js')) {
                const url = new URL(scripts[i].src);
                walletURL = url.origin + '/';
                break;
            }
        }
        return walletURL;
    }
    /**
     * Gets the wallet appId
     */
    static getAppId() {
        let appId = null;
        const scripts = document.getElementsByTagName('script');
        for (let i = 0; i < scripts.length; i++) {
            if (scripts[i].src.includes('fun-wallet-sdk.js')) {
                const url = new URL(scripts[i].src);
                appId = this.getParameterByName('appId', url.href);
                break;
            }
        }
        return appId;
    }
    /**
     * Get meta tag info
     * @param metaName The meta tag name
     */
    static getMeta(metaName) {
        const metas = document.getElementsByTagName('meta');
        for (let i = 0; i < metas.length; i++) {
            if (metas[i].getAttribute('name') === metaName) {
                return metas[i].getAttribute('content');
            }
        }
        return null;
    }
    /**
     * Adds the css file to the head of the dom
     * @param fileName The file name
     */
    static addCssToHead(fileName) {
        const head = document.head;
        const link = document.createElement('link');
        link.type = 'text/css';
        link.rel = 'stylesheet';
        link.href = fileName;
        head.appendChild(link);
    }
    /**
     * Get leader html iframe element from the dom
     */
    static getLeaderHTMLIFrameElement() {
        const allFunWalletIFrames = this.getAllWalletIFrames();
        const leaders = allFunWalletIFrames.filter((l) => l.attributes.getNamedItem(Constants.isLeaderAttributeName) !== null);
        if (leaders.length === 0) {
            throw new Error('You do not have a leader instance, the integration you have done is incorrect');
        }
        if (leaders.length > 1) {
            throw new Error(`You already have ${leaders.length} wallet leader instances in context, you're only allowed 1 leader instance`);
        }
        return leaders[0];
    }
    /**
     * Get leader html iframe element from the dom
     */
    static getFollowerHTMLIFrameElement() {
        const allFunWalletIFrames = Helpers.getAllWalletIFrames();
        const followers = allFunWalletIFrames.filter((l) => l.attributes.getNamedItem(Constants.isFollowerAttributeName) !== null);
        if (followers.length === 0) {
            throw new Error('You do not have a follower instance visible in the dom, the integration you have done is incorrect');
        }
        if (followers.length > 1) {
            throw new Error(`You already have ${followers.length} wallet follower instances in context, you're only allowed 1 follower instance at a time`);
        }
        return followers[0];
    }
    /**
     * Get all iframe elements which are a fun wallet
     */
    static getAllWalletIFrames() {
        const walletIframes = [];
        const iframes = document.getElementsByTagName('iframe');
        for (let i = 0; i < iframes.length; i++) {
            if (iframes[i].attributes &&
                iframes[i].attributes.getNamedItem(Constants.isFunWalletAttributeName) !== null) {
                walletIframes.push(iframes[i]);
            }
        }
        return walletIframes;
    }
    /**
     * Build up wallet instance url
     * @param walletInstanceBuilder The wallet instance builder
     */
    static buildUpWalletInstanceURL(walletInstanceBuilder = {}) {
        const walletUrl = Store.url;
        if (walletUrl === '') {
            throw new Error('Fun wallet url can not be found - your integration is not correct');
        }
        if (walletInstanceBuilder.isPopUp) {
            return `${walletUrl}sign-in${this.buildWalletQueryParameters()}`;
        }
        if (walletInstanceBuilder.isLeader) {
            return `${walletUrl}embedded-authentication${this.buildWalletQueryParameters()}&leader=true`;
        }
        return `${walletUrl}follower-loading${this.buildWalletQueryParameters()}${this.buildWalletRouteDeepLink()}`;
    }
    /**
     * Pop the window opener and center
     * @param url The url
     * @param title The title
     * @param w The width
     * @param h The height
     */
    static openWindowPopupAndCenter(url, title, w, h) {
        const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
        const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;
        const width = window.innerWidth
            ? window.innerWidth
            : document.documentElement.clientWidth
                ? document.documentElement.clientWidth
                : screen.width;
        const height = window.innerHeight
            ? window.innerHeight
            : document.documentElement.clientHeight
                ? document.documentElement.clientHeight
                : screen.height;
        const systemZoom = width / window.screen.availWidth;
        const left = (width - w) / 2 / systemZoom + dualScreenLeft;
        const top = (height - h) / 2 / systemZoom + dualScreenTop;
        const newWindow = window.open(url, title, `
      scrollbars=no,
      resizable=no,
      status=no,
      location=no,
      toolbar=no,
      menubar=no,
      width=${w / systemZoom},
      height=${h / systemZoom},
      top=${top},
      left=${left}
      `);
        if (window.focus !== undefined && newWindow) {
            newWindow.focus();
        }
        return newWindow;
    }
    /**
     * Get the origin from a full url
     * @param href The href url
     */
    static getOrigin(href) {
        const l = document.createElement('a');
        l.href = href;
        return l.origin;
    }
    /**
     * Get parameter by name
     * @param name The parameter name
     * @param url The url - default it will get `window.location.href`
     */
    static getParameterByName(name, url = window.location.href) {
        if (!url) {
            url = window.location.href;
        }
        name = name.replace(/[\[\]]/g, '\\$&');
        const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'), results = regex.exec(url);
        if (!results) {
            return null;
        }
        if (!results[2]) {
            return '';
        }
        const result = results[2].replace(/\+/g, ' ');
        return Helpers.decodeIfNeeded(result);
    }
    /**
     * Decode if needed if it throws return number result
     * @param result The result
     */
    // tslint:disable-next-line: no-any
    static decodeIfNeeded(result) {
        try {
            return decodeURIComponent(result);
        }
        catch (error) {
            return result;
        }
    }
    /**
     * Deep clone a object
     * @param object The object
     */
    static deepClone(object) {
        return JSON.parse(JSON.stringify(object));
    }
    /**
     * Get the width of the page
     */
    static getScreenCurrentWidth() {
        return Math.max(document.body.scrollWidth, document.documentElement.scrollWidth, document.body.offsetWidth, document.documentElement.offsetWidth, document.documentElement.clientWidth);
    }
    /**
     * Get ethereum provider
     */
    static getProvider() {
        return window.funwallet.sdk.ethereum;
    }
    /**
     * Build wallet query parameters
     */
    static buildWalletQueryParameters() {
        return `?identifier=${Store.instanceIdentifier}&${this.getReferrerParameter()}&${this.buildUpLanguageParameter()}${this.buildUpUserTrackingId()}${this.buildUpAppId()}`;
    }
    /**
     * Build up language parameter
     */
    static buildUpLanguageParameter() {
        return `lang=${Store.language}`;
    }
    /**
     * Build up user tracking id
     */
    static buildUpUserTrackingId() {
        if (Store.userTrackingId) {
            return `&track=${Store.userTrackingId}`;
        }
        return '';
    }
    /**
     * Build up app id
     */
    static buildUpAppId() {
        if (Store.appId) {
            return `&appId=${Store.appId}`;
        }
        return '';
    }
    /**
     * Get the referrer parameter
     */
    static getReferrerParameter() {
        return `referrer=${window.location.host}`;
    }
    /**
     * Get the route deep link
     */
    static buildWalletRouteDeepLink() {
        const deepLinkRouteTo = this.getParameterByName(Constants.funWalletRouteQueryParameterName);
        if (deepLinkRouteTo) {
            return `&routeTo=${deepLinkRouteTo}`;
        }
        return '';
    }
}
//# sourceMappingURL=helpers.js.map